import React, { FC, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Heading1, Breadcrumbs } from '@blueprint/ui';

import {
    SearchResultsHeaderWrapper,
    SearchResultsTotalVariant,
    Footer,
    FooterCell,
    BreadcrumbsShowDesktopWrapper
} from './SearchResultsHeader.styles';
import { totalResultsTextSelector } from '../../../reducers/searchResults';
import { FiltersModal } from '../../FiltersModal';
import { Breadcrumb, Links } from '../../SearchContext';
import { EditSavedSearchBanner } from '../../banners/EditSavedSearchBanner';
import { SectionThatAppearsWhenNoSideBar } from '../../Layout/Layout.styles';
import { isShowEditSavedSearchBanner } from '../../helpers';
import { LayoutVersion } from '../../../constants';
import { AlertPanelMobile } from 'search/AlertPanelMobile/variant';

export type SearchResultsHeaderProps = {
    breadcrumbs: Breadcrumb[];
    pageTitle: string;
    isEditSavedSearchBannerVisible: boolean;
    userAlertId: string | null;
    previousSavedSearchUri: string | null;
    savedSearchAndAlertsUri: string | undefined;
    isLoggedIn: boolean | null;
    sortOrderDropdown: React.ReactElement;
    links: Links;
};

export const SearchResultsHeader: FC<SearchResultsHeaderProps> = ({
    breadcrumbs,
    pageTitle,
    isEditSavedSearchBannerVisible,
    userAlertId,
    previousSavedSearchUri,
    savedSearchAndAlertsUri,
    isLoggedIn,
    sortOrderDropdown,
    links
}) => {
    const totalResultsText = useSelector(totalResultsTextSelector);

    const [showEditSavedSearchBanner, setShowEditSavedSearchBanner] = useState(
        false
    );

    useEffect(() => {
        if (!isEditSavedSearchBannerVisible) {
            setShowEditSavedSearchBanner(false);
        } else if (isLoggedIn) {
            const showEditSavedSearch = isShowEditSavedSearchBanner(
                userAlertId,
                previousSavedSearchUri,
                savedSearchAndAlertsUri
            );

            setShowEditSavedSearchBanner(!!showEditSavedSearch);
        }
    }, [
        isLoggedIn,
        isEditSavedSearchBannerVisible,
        userAlertId,
        previousSavedSearchUri,
        savedSearchAndAlertsUri
    ]);

    return (
        <SearchResultsHeaderWrapper data-testid="search-results-header-variant">
            <BreadcrumbsShowDesktopWrapper>
                <Breadcrumbs
                    data-testid="desktop-breadcrumbs"
                    data={breadcrumbs}
                />
            </BreadcrumbsShowDesktopWrapper>

            {showEditSavedSearchBanner ? (
                <SectionThatAppearsWhenNoSideBar data-testid="search-results-header_edit-saved-search-cta">
                    <EditSavedSearchBanner />
                </SectionThatAppearsWhenNoSideBar>
            ) : null}

            <Heading1 data-testid={`results-title`} isRegular>
                {pageTitle}
            </Heading1>
            <AlertPanelMobile links={links} />
            <Footer>
                <SearchResultsTotalVariant
                    data-testid="total-results"
                    size="secondary"
                >
                    {totalResultsText}
                </SearchResultsTotalVariant>
                <FooterCell>
                    <FiltersModal
                        version={LayoutVersion.Desktop}
                        testId="search-results-header_filters-button"
                    />
                    {sortOrderDropdown}
                </FooterCell>
            </Footer>
        </SearchResultsHeaderWrapper>
    );
};
